<script lang="ts" generics="T">
  import { tick } from 'svelte'

  // Drop-in replacement for a native <select class="select">, styled so the
  // highlighted option always reads the same way across every browser/OS —
  // a native <select>'s open popup is rendered by the OS/browser chrome, so
  // its highlight color can't be controlled from CSS at all.
  interface Option<T> {
    value: T
    label: string
  }

  let {
    options,
    value = $bindable(),
    id,
    disabled = false,
    onchange,
  }: {
    options: Option<T>[]
    value: T
    id?: string
    disabled?: boolean
    // Most callers just use bind:value. A few own their state elsewhere and
    // want a callback instead (mirrors the native <select>'s
    // value={x} onchange={...} pattern already used in a couple of places).
    onchange?: (value: T) => void
  } = $props()

  let open = $state(false)
  let activeIndex = $state(-1)
  let wrapperEl = $state<HTMLDivElement | undefined>(undefined)
  let menuEl = $state<HTMLUListElement | undefined>(undefined)
  const listboxId = `dropdown-${Math.random().toString(36).slice(2, 10)}`

  const selectedLabel = $derived(options.find((o) => o.value === value)?.label ?? '')
  const activeId = $derived(activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined)

  function scrollActiveIntoView() {
    const item = menuEl?.children[activeIndex] as HTMLElement | undefined
    if (!menuEl || !item) return
    const menuRect = menuEl.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    if (itemRect.top < menuRect.top) {
      menuEl.scrollTop -= menuRect.top - itemRect.top
    } else if (itemRect.bottom > menuRect.bottom) {
      menuEl.scrollTop += itemRect.bottom - menuRect.bottom
    }
  }

  // The menu always opens downward at full height. If it extends below the
  // visible area, scroll the page just enough to show it; the pager reserves
  // room beneath itself so this stays inside the layout.
  function fitMenu() {
    menuEl?.scrollIntoView({ block: 'nearest' })
  }

  async function toggle() {
    if (disabled) return
    if (!open) {
      activeIndex = options.findIndex((o) => o.value === value)
      open = true
      await tick()
      fitMenu()
      scrollActiveIntoView()
    } else {
      open = false
    }
  }

  function pick(opt: Option<T>) {
    value = opt.value
    onchange?.(opt.value)
    open = false
  }

  function moveActive(delta: number) {
    if (options.length === 0) return
    activeIndex = (activeIndex + delta + options.length) % options.length
  }

  async function onKeydown(e: KeyboardEvent) {
    if (disabled) return
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        open = true
        activeIndex = options.findIndex((o) => o.value === value)
        if (activeIndex === -1) activeIndex = 0
        await tick()
        fitMenu()
        scrollActiveIntoView()
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveActive(1)
      await tick()
      scrollActiveIntoView()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveActive(-1)
      await tick()
      scrollActiveIntoView()
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const opt = options[activeIndex]
      if (opt) pick(opt)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      open = false
    } else if (e.key === 'Tab') {
      open = false
    }
  }

  function onFocusOut(e: FocusEvent) {
    const next = e.relatedTarget as Node | null
    if (next && wrapperEl?.contains(next)) return
    open = false
  }
</script>

<div class="dropdown" bind:this={wrapperEl} onfocusout={onFocusOut}>
  <button
    type="button"
    class="select dropdown__trigger"
    {id}
    {disabled}
    role="combobox"
    aria-haspopup="listbox"
    aria-expanded={open}
    aria-controls={listboxId}
    aria-activedescendant={activeId}
    onclick={toggle}
    onkeydown={onKeydown}
  >
    <!-- Invisible stack of every option's label, so the button's own
         intrinsic width is the widest of all of them — same as a native
         <select>. Without this, the box would resize (and could clip text)
         as the selection changes, since only the current label would
         otherwise contribute to its width. -->
    <span class="dropdown__sizer" aria-hidden="true">
      {#each options as opt, i (i)}
        <span>{opt.label}</span>
      {/each}
    </span>
    <span class="dropdown__value">{selectedLabel}</span>
    <span class="dropdown__caret" aria-hidden="true">▾</span>
  </button>

  {#if open}
    <ul class="dropdown__menu" role="listbox" id={listboxId} bind:this={menuEl}>
      {#each options as opt, i (i)}
        <li
          id="{listboxId}-opt-{i}"
          class="dropdown__item"
          class:dropdown__item--active={i === activeIndex}
          role="option"
          aria-selected={opt.value === value}
          onmouseenter={() => (activeIndex = i)}
          onmousedown={(e) => {
            // Select on mousedown, not click: prevents any focus/blur race
            // between mousedown and click from leaving the menu open until a
            // separate outside click closes it.
            e.preventDefault()
            pick(opt)
          }}
        >
          {opt.label}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .dropdown {
    position: relative;
  }

  .dropdown__trigger {
    display: grid;
    grid-template-columns: auto auto;
    column-gap: v.$space-sm;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    text-align: left;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  // Stacked in the same grid cell as .dropdown__value (both grid-column: 1)
  // so that cell's auto width is the max of every option's label — see the
  // comment on the markup above.
  .dropdown__sizer {
    grid-column: 1;
    grid-row: 1;
    display: grid;
    visibility: hidden;

    span {
      grid-column: 1;
      grid-row: 1;
      white-space: nowrap;
    }
  }

  .dropdown__value {
    grid-column: 1;
    grid-row: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dropdown__caret {
    grid-column: 2;
    grid-row: 1;
    font-size: 0.7rem;
    color: v.$color-text-muted;
  }

  .dropdown__menu {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    z-index: 20;
    list-style: none;
    margin: 0;
    padding: v.$space-xs 0;
    max-height: 16rem;
    overflow-y: auto;
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border-strong;
    border-radius: v.$radius-sm;
    box-shadow: v.$shadow-md;
  }

  .dropdown__item {
    padding: v.$space-xs v.$space-md;
    font-size: 0.8125rem;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    // Hover / keyboard highlight uses the shared light green; the currently
    // selected option uses the stronger green and keeps it while hovered.
    &--active {
      @include v.interactive-hover;
    }

    &[aria-selected='true'],
    &[aria-selected='true'].dropdown__item--active {
      @include v.interactive-selected;
    }
  }
</style>
